import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, Flex, Spinner, Center, Text, IconButton, Tooltip,
    ButtonGroup, Modal, ModalOverlay, ModalContent, ModalHeader,
    ModalBody, ModalCloseButton, Grid, GridItem, Badge,
    useBreakpointValue,
} from '@chakra-ui/react';
import {
    MdMyLocation,
    MdOutlineKeyboardArrowLeft,
    MdOutlineKeyboardArrowRight,
} from 'react-icons/md';
import { FiPlus, FiMinus } from 'react-icons/fi';
import * as maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import * as turf from '@turf/turf';

import { UpkeepMapRaw, UpkeepMapFilters } from '../types';
import { MONTH_COLORS } from '../apiConfig';
import { useUpkeepMap } from '../hooks/useUpkeepMap';
import FilterUpkeepMap from '../components/FilterUpkeepMap';
import UpkeepMapLegend from '../components/UpkeepMapLegend';
import Navbar from '../../../components/Navbar';

const MAP_CENTER: [number, number] = [104.083064, -3.125992];
const MAP_ZOOM = 10.9;
const DEFAULT_BLOCK_COLOR = '#e8e8e8';
const ICON_SIZE = 26;

function drawIcon(
    type: 'full' | 'half' | 'tri',
    color: string,
): ImageData {
    const canvas = document.createElement('canvas');
    canvas.width = ICON_SIZE;
    canvas.height = ICON_SIZE;
    const ctx = canvas.getContext('2d')!;
    const cx = ICON_SIZE / 2;
    const cy = ICON_SIZE / 2;
    const r = ICON_SIZE / 2 - 3;

    ctx.fillStyle = color;
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 2;

    if (type === 'full') {
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
    } else if (type === 'half') {
        // clockwise (false) from 6-o'clock through 9-o'clock to 12-o'clock = left arc
        ctx.beginPath();
        ctx.arc(cx, cy, r, Math.PI / 2, -Math.PI / 2, false);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
    } else {
        // Triangle pointing up
        ctx.beginPath();
        ctx.moveTo(cx, 2);
        ctx.lineTo(2, ICON_SIZE - 2);
        ctx.lineTo(ICON_SIZE - 2, ICON_SIZE - 2);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
    }

    return ctx.getImageData(0, 0, ICON_SIZE, ICON_SIZE);
}

function generateMarkerImages(mapInstance: maplibregl.Map) {
    Object.entries(MONTH_COLORS).forEach(([month, color]) => {
        mapInstance.addImage(`mk-full-${month}`, drawIcon('full', color));
        mapInstance.addImage(`mk-half-${month}`, drawIcon('half', color));
        mapInstance.addImage(`mk-tri-${month}`, drawIcon('tri', color));
    });
}

// marker layout constants

// Centered stack offsets (step=10) for N=1..6 periods
// step=10 with r=10: circles overlap 50% (10/20px), D-terbalik shapes touch exactly
const STACK_OFFSETS: number[][] = [
    [0],
    [-5, 5],
    [-10, 0, 10],
    [-15, -5, 5, 15],
    [-20, -10, 0, 10, 20],
    [-25, -15, -5, 5, 15, 25],
];
const SIDE_SPLIT = 18; // pixel gap between main & ref group centres when both present

// Pre-build match expression covering every possible ox value
// so MapLibre can use a numeric feature property as icon-offset
const OFFSET_EXPR: any[] = (() => {
    const vals = new Set<number>();
    STACK_OFFSETS.forEach(arr => arr.forEach(v => {
        vals.add(v);
        vals.add(v - SIDE_SPLIT);
        vals.add(v + SIDE_SPLIT);
    }));
    const expr: any[] = ['match', ['get', 'ox']];
    Array.from(vals).sort((a, b) => a - b).forEach(v => {
        expr.push(v, ['literal', [v, 0]]);
    });
    expr.push(['literal', [0, 0]]); // default
    return expr;
})();

// data helpers

interface BlockPeriodMarker { month: string; isFull: boolean; }
interface BlockMarkerInfo   { budgetMonth: string; periods: BlockPeriodMarker[]; }

function getPeriodMonth(periode: string): string {
    return periode.slice(4, 6);
}

function formatPeriode(periode: string): string {
    const year = periode.slice(0, 4);
    const month = periode.slice(4, 6);
    const monthNames: Record<string, string> = {
        '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr',
        '05': 'Mei', '06': 'Jun', '07': 'Jul', '08': 'Ags',
        '09': 'Sep', '10': 'Okt', '11': 'Nov', '12': 'Des',
    };
    return `${monthNames[month] ?? month} ${year}`;
}

// Returns per-month periods so each work month gets its own icon
function groupByBlockForMarkers(rows: UpkeepMapRaw[]): Map<string, BlockMarkerInfo> {
    const blockMap = new Map<string, UpkeepMapRaw[]>();
    rows.forEach(row => {
        if (!blockMap.has(row.block)) blockMap.set(row.block, []);
        blockMap.get(row.block)!.push(row);
    });

    const result = new Map<string, BlockMarkerInfo>();

    blockMap.forEach((blockRows, block) => {
        const bgtRows = blockRows.filter(r => r.work_bgt > 0);
        if (bgtRows.length === 0) return;

        bgtRows.sort((a, b) => a.periode.localeCompare(b.periode));
        const budgetMonth = getPeriodMonth(bgtRows[bgtRows.length - 1].periode);

        // Aggregate budget and actual per month
        const monthBgt = new Map<string, number>();
        bgtRows.forEach(r => {
            const m = getPeriodMonth(r.periode);
            monthBgt.set(m, (monthBgt.get(m) ?? 0) + r.work_bgt);
        });
        const monthAct = new Map<string, number>();
        blockRows.filter(r => r.work_act > 0).forEach(r => {
            const m = getPeriodMonth(r.periode);
            monthAct.set(m, (monthAct.get(m) ?? 0) + r.work_act);
        });

        const periods: BlockPeriodMarker[] = Array.from(monthAct.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([month, act]) => ({
                month,
                isFull: Math.round(act * 100) >= Math.round((monthBgt.get(month) ?? 0) * 100),
            }));

        result.set(block, { budgetMonth, periods });
    });

    return result;
}

function getBlockCenter(feature: GeoJSON.Feature): [number, number] | null {
    try {
        const center = turf.centerOfMass(feature as any);
        const [lng, lat] = center.geometry.coordinates;
        if (isNaN(lng) || isNaN(lat)) return null;
        return [lng, lat];
    } catch {
        return null;
    }
}

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

// -----------------------------------------------------------------------

const UpkeepMapPage: React.FC = () => {
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<maplibregl.Map | null>(null);

    // Pre-computed block centers from GeoJSON files (avoids re-iterating on every data update)
    const blockCentersRef = useRef<Map<string, [number, number]>>(new Map());

    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [isSatellite, setIsSatellite] = useState(false);

    const [selectedBlockCode, setSelectedBlockCode] = useState<string | null>(null);
    const [selectedBlockRows, setSelectedBlockRows] = useState<UpkeepMapRaw[] | null>(null);

    const { data, referenceData, loading, filters, fetchData, updateFilters, clearFilters } = useUpkeepMap();
    const filtersRef = useRef<UpkeepMapFilters>(filters);
    filtersRef.current = filters;
    const dataRef = useRef<UpkeepMapRaw[]>(data);
    dataRef.current = data;
    const referenceDataRef = useRef<UpkeepMapRaw[]>(referenceData);
    referenceDataRef.current = referenceData;

    const defaultSidebarOpen = useBreakpointValue({ base: false, md: true });
    useEffect(() => {
        if (defaultSidebarOpen !== undefined) setSidebarOpen(defaultSidebarOpen);
    }, [defaultSidebarOpen]);

    const handleBlockClick = useCallback((blockCode: string) => {
        const rows = dataRef.current.filter(r => r.block === blockCode);
        if (rows.length === 0) return;
        setSelectedBlockCode(blockCode);
        setSelectedBlockRows(rows);
    }, []);

    const handleReferenceBlockClick = useCallback((blockCode: string) => {
        const rows = referenceDataRef.current.filter(r => r.block === blockCode);
        if (rows.length === 0) return;
        setSelectedBlockCode(blockCode);
        setSelectedBlockRows(rows);
    }, []);

    // Init map
    useEffect(() => {
        if (!mapContainerRef.current) return;

        const mapInstance = new maplibregl.Map({
            container: mapContainerRef.current,
            style: 'https://api.maptiler.com/maps/streets-v2/style.json?key=Q3fXi2sghTMg1wQSFgxK',
            center: MAP_CENTER,
            zoom: MAP_ZOOM,
            attributionControl: false,
        });

        mapInstance.on('load', () => {
            // Satellite source
            mapInstance.addSource('satellite', {
                type: 'raster',
                tiles: ['https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=Q3fXi2sghTMg1wQSFgxK'],
                tileSize: 256,
            });
            mapInstance.addLayer({
                id: 'satellite-layer',
                type: 'raster',
                source: 'satellite',
                layout: { visibility: 'none' },
            });

            // Pre-generate all marker icon images (WebGL sprites)
            generateMarkerImages(mapInstance);

            loadGeoJSON(mapInstance);
        });

        setMap(mapInstance);
        return () => mapInstance.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadGeoJSON = async (mapInstance: maplibregl.Map) => {
        // GBS blocks
        try {
            const res = await fetch('/data/batas_blok_kebun_gbs.geojson');
            const geo = await res.json();
            mapInstance.addSource('geojson-source', { type: 'geojson', data: geo });
            mapInstance.addLayer({
                id: 'geojson-layer',
                type: 'fill',
                source: 'geojson-source',
                paint: {
                    'fill-color': ['case', ['has', 'color'], ['get', 'color'], DEFAULT_BLOCK_COLOR],
                    'fill-opacity': 0.65,
                    'fill-outline-color': '#555555',
                },
            });
            mapInstance.addLayer({
                id: 'geojson-label',
                type: 'symbol',
                source: 'geojson-source',
                layout: {
                    'text-field': ['get', 'LandUse_26'],
                    'text-size': 11,
                    'text-anchor': 'bottom',
                    'text-offset': [0, -1.5],
                },
                paint: { 'text-color': '#1a1a1a', 'text-halo-width': 1.5, 'text-halo-color': '#fff' },
            });

            mapInstance.on('click', 'geojson-layer', (e) => {
                const code: string = e.features?.[0]?.properties?.LandUse_26 ?? '';
                if (code) handleBlockClick(code);
            });
            mapInstance.on('mouseenter', 'geojson-layer', () => { mapInstance.getCanvas().style.cursor = 'pointer'; });
            mapInstance.on('mouseleave', 'geojson-layer', () => { mapInstance.getCanvas().style.cursor = ''; });

            // Pre-compute block centers for GBS
            geo.features.forEach((f: any) => {
                const code: string = f.properties?.LandUse_26 ?? '';
                if (code && !blockCentersRef.current.has(code)) {
                    const center = getBlockCenter(f as GeoJSON.Feature);
                    if (center) blockCentersRef.current.set(code, center);
                }
            });
        } catch (e) {
            console.error('Error loading GBS GeoJSON:', e);
        }

        // LKK blocks
        try {
            const res = await fetch('/data/Blok_Planted_LKK.geojson');
            const lkk = await res.json();
            mapInstance.addSource('lkk-source', { type: 'geojson', data: lkk });
            mapInstance.addLayer({
                id: 'lkk-layer',
                type: 'fill',
                source: 'lkk-source',
                paint: {
                    'fill-color': ['case', ['has', 'color'], ['get', 'color'], DEFAULT_BLOCK_COLOR],
                    'fill-opacity': 0.65,
                    'fill-outline-color': '#555555',
                },
            });
            mapInstance.addLayer({
                id: 'lkk-label',
                type: 'symbol',
                source: 'lkk-source',
                layout: {
                    'text-field': ['get', 'blok_code'],
                    'text-size': 11,
                    'text-anchor': 'bottom',
                    'text-offset': [0, -1.5],
                },
                paint: { 'text-color': '#1a1a1a', 'text-halo-width': 1.5, 'text-halo-color': '#fff' },
            });

            mapInstance.on('click', 'lkk-layer', (e) => {
                const raw: string = e.features?.[0]?.properties?.blok_code ?? '';
                const code = raw.length === 8 ? raw.slice(0, 6) : raw;
                if (code) handleBlockClick(code);
            });
            mapInstance.on('mouseenter', 'lkk-layer', () => { mapInstance.getCanvas().style.cursor = 'pointer'; });
            mapInstance.on('mouseleave', 'lkk-layer', () => { mapInstance.getCanvas().style.cursor = ''; });

            // Pre-compute block centers for LKK
            lkk.features.forEach((f: any) => {
                const raw: string = f.properties?.blok_code ?? '';
                const code = raw.length === 8 ? raw.slice(0, 6) : raw;
                if (code && !blockCentersRef.current.has(code)) {
                    const center = getBlockCenter(f as GeoJSON.Feature);
                    if (center) blockCentersRef.current.set(code, center);
                }
            });
        } catch (e) {
            console.error('Error loading LKK GeoJSON:', e);
        }

        // MRN blocks
        try {
            const res = await fetch('/data/mrn_map.geojson');
            const mrn = await res.json();
            mapInstance.addSource('mrn-source', { type: 'geojson', data: mrn });
            mapInstance.addLayer({
                id: 'mrn-layer',
                type: 'fill',
                source: 'mrn-source',
                paint: {
                    'fill-color': ['case', ['has', 'color'], ['get', 'color'], DEFAULT_BLOCK_COLOR],
                    'fill-opacity': 0.65,
                    'fill-outline-color': '#555555',
                },
            });
            mapInstance.addLayer({
                id: 'mrn-label',
                type: 'symbol',
                source: 'mrn-source',
                layout: {
                    'text-field': ['coalesce', ['get', 'blok_code'], ['get', 'LandUse_26'], ''],
                    'text-size': 11,
                    'text-anchor': 'bottom',
                    'text-offset': [0, -1.5],
                },
                paint: { 'text-color': '#1a1a1a', 'text-halo-width': 1.5, 'text-halo-color': '#fff' },
            });

            mapInstance.on('click', 'mrn-layer', (e) => {
                const raw: string = e.features?.[0]?.properties?.blok_code ?? e.features?.[0]?.properties?.LandUse_26 ?? '';
                const code = raw.length === 8 ? raw.slice(0, 6) : raw;
                if (code) handleBlockClick(code);
            });
            mapInstance.on('mouseenter', 'mrn-layer', () => { mapInstance.getCanvas().style.cursor = 'pointer'; });
            mapInstance.on('mouseleave', 'mrn-layer', () => { mapInstance.getCanvas().style.cursor = ''; });

            // Pre-compute block centers for MRN
            mrn.features.forEach((f: any) => {
                const raw: string = f.properties?.blok_code ?? f.properties?.LandUse_26 ?? '';
                const code = raw.length === 8 ? raw.slice(0, 6) : raw;
                if (code && !blockCentersRef.current.has(code)) {
                    const center = getBlockCenter(f as GeoJSON.Feature);
                    if (center) blockCentersRef.current.set(code, center);
                }
            });
        } catch (e) {
            console.error('Error loading MRN GeoJSON:', e);
        }

        // Markers source + symbol layers (WebGL — no DOM overhead)
        mapInstance.addSource('markers-source', { type: 'geojson', data: EMPTY_FC });

        const iconSizeExpr: any = [
            'interpolate', ['linear'], ['zoom'],
            8,  0.2,
            11, 0.6,
            15, 0.9,
        ];

        // Main activity markers (circles)
        mapInstance.addLayer({
            id: 'main-markers',
            type: 'symbol',
            source: 'markers-source',
            filter: ['==', ['get', 'mt'], 'main'],
            layout: {
                'icon-image': ['get', 'iconId'] as any,
                'icon-size': iconSizeExpr,
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
                'icon-offset': OFFSET_EXPR as any,
            },
        });

        // Reference activity markers (triangles)
        mapInstance.addLayer({
            id: 'ref-markers',
            type: 'symbol',
            source: 'markers-source',
            filter: ['==', ['get', 'mt'], 'ref'],
            layout: {
                'icon-image': ['get', 'iconId'] as any,
                'icon-size': iconSizeExpr,
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
                'icon-offset': OFFSET_EXPR as any,
            },
        });

        mapInstance.on('click', 'main-markers', (e) => {
            const code: string = e.features?.[0]?.properties?.block ?? '';
            if (code) handleBlockClick(code);
        });
        mapInstance.on('click', 'ref-markers', (e) => {
            const code: string = e.features?.[0]?.properties?.block ?? '';
            if (code) handleReferenceBlockClick(code);
        });
        ['main-markers', 'ref-markers'].forEach(layerId => {
            mapInstance.on('mouseenter', layerId, () => { mapInstance.getCanvas().style.cursor = 'pointer'; });
            mapInstance.on('mouseleave', layerId, () => { mapInstance.getCanvas().style.cursor = ''; });
        });
    };

    // Apply block colors + markers whenever data changes
    useEffect(() => {
        if (!map || !map.isStyleLoaded()) return;

        const blockDisplayMap    = groupByBlockForMarkers(data);
        const refBlockDisplayMap = groupByBlockForMarkers(referenceData);

        const colorForBlock = (code: string) => {
            const main = blockDisplayMap.get(code);
            if (main) return MONTH_COLORS[main.budgetMonth] ?? DEFAULT_BLOCK_COLOR;
            const ref = refBlockDisplayMap.get(code);
            if (ref) return MONTH_COLORS[ref.budgetMonth] ?? DEFAULT_BLOCK_COLOR;
            return DEFAULT_BLOCK_COLOR;
        };

        const geoSource = map.getSource('geojson-source') as maplibregl.GeoJSONSource;
        if (geoSource) {
            try {
                const geoData: any = (geoSource as any)._data;
                if (geoData?.features) {
                    geoData.features.forEach((f: any) => {
                        const code: string = f.properties?.LandUse_26 ?? '';
                        f.properties.color = colorForBlock(code);
                    });
                    geoSource.setData(geoData);
                }
            } catch (e) { console.error(e); }
        }

        const lkkSource = map.getSource('lkk-source') as maplibregl.GeoJSONSource;
        if (lkkSource) {
            try {
                const lkkData: any = (lkkSource as any)._data;
                if (lkkData?.features) {
                    lkkData.features.forEach((f: any) => {
                        const raw: string = f.properties?.blok_code ?? '';
                        const code = raw.length === 8 ? raw.slice(0, 6) : raw;
                        f.properties.color = colorForBlock(code);
                    });
                    lkkSource.setData(lkkData);
                }
            } catch (e) { console.error(e); }
        }

        const mrnSource = map.getSource('mrn-source') as maplibregl.GeoJSONSource;
        if (mrnSource) {
            try {
                const mrnData: any = (mrnSource as any)._data;
                if (mrnData?.features) {
                    mrnData.features.forEach((f: any) => {
                        const raw: string = f.properties?.blok_code ?? f.properties?.LandUse_26 ?? '';
                        const code = raw.length === 8 ? raw.slice(0, 6) : raw;
                        f.properties.color = colorForBlock(code);
                    });
                    mrnSource.setData(mrnData);
                }
            } catch (e) { console.error(e); }
        }

        // Build marker GeoJSON — one feature per actual-work month per block
        const markerFeatures: GeoJSON.Feature[] = [];
        const hasRefData = referenceData.length > 0;
        const centers = blockCentersRef.current;

        const allCodes = new Set<string>([
            ...Array.from(blockDisplayMap.keys()),
            ...(hasRefData ? Array.from(refBlockDisplayMap.keys()) : []),
        ]);

        allCodes.forEach(code => {
            const center = centers.get(code);
            if (!center) return;

            const mainInfo = blockDisplayMap.get(code);
            const refInfo  = hasRefData ? refBlockDisplayMap.get(code) : undefined;

            const mainPeriods = mainInfo?.periods ?? [];
            const refPeriods  = refInfo?.periods  ?? [];
            if (mainPeriods.length === 0 && refPeriods.length === 0) return;

            const inSplit = mainPeriods.length > 0 && refPeriods.length > 0;
            const mainBaseOx =  inSplit ? SIDE_SPLIT : 0;
            const refBaseOx  = inSplit ? -SIDE_SPLIT : 0;

            // One circle / half-circle per actual month — push right-to-left so
            // rightmost feature is first in array (MapLibre renders earlier = on top)
            if (mainPeriods.length > 0) {
                const stk = STACK_OFFSETS[Math.min(mainPeriods.length, STACK_OFFSETS.length) - 1];
                for (let i = mainPeriods.length - 1; i >= 0; i--) {
                    const p = mainPeriods[i];
                    markerFeatures.push({
                        type: 'Feature',
                        geometry: { type: 'Point', coordinates: center },
                        properties: {
                            block: code,
                            mt: 'main',
                            iconId: p.isFull ? `mk-full-${p.month}` : `mk-half-${p.month}`,
                            ox: mainBaseOx + (stk[i] ?? stk[stk.length - 1]),
                        },
                    });
                }
            }

            // One triangle per actual month — same right-to-left push order
            if (refPeriods.length > 0) {
                const stk = STACK_OFFSETS[Math.min(refPeriods.length, STACK_OFFSETS.length) - 1];
                for (let i = refPeriods.length - 1; i >= 0; i--) {
                    const p = refPeriods[i];
                    markerFeatures.push({
                        type: 'Feature',
                        geometry: { type: 'Point', coordinates: center },
                        properties: {
                            block: code,
                            mt: 'ref',
                            iconId: `mk-tri-${p.month}`,
                            ox: refBaseOx + (stk[i] ?? stk[stk.length - 1]),
                        },
                    });
                }
            }
        });

        const markersSource = map.getSource('markers-source') as maplibregl.GeoJSONSource;
        if (markersSource) {
            markersSource.setData({ type: 'FeatureCollection', features: markerFeatures });
        }

    }, [data, referenceData, map]);

    const handleApply = useCallback((newFilters: UpkeepMapFilters) => {
        updateFilters(newFilters);
        fetchData(newFilters);
    }, [updateFilters, fetchData]);

    const handleClear = useCallback(() => {
        clearFilters();
        if (map) {
            const resetSource = (sourceId: string) => {
                const src = map.getSource(sourceId) as maplibregl.GeoJSONSource;
                if (!src) return;
                const gd: any = (src as any)._data;
                if (gd?.features) {
                    gd.features.forEach((f: any) => { f.properties.color = DEFAULT_BLOCK_COLOR; });
                    src.setData(gd);
                }
            };
            resetSource('geojson-source');
            resetSource('lkk-source');
            resetSource('mrn-source');
            const markersSource = map.getSource('markers-source') as maplibregl.GeoJSONSource;
            if (markersSource) markersSource.setData(EMPTY_FC);
        }
    }, [clearFilters, map]);

    const toggleSatellite = useCallback(() => {
        if (!map) return;
        const newState = !isSatellite;
        setIsSatellite(newState);
        map.setLayoutProperty('satellite-layer', 'visibility', newState ? 'visible' : 'none');
    }, [map, isSatellite]);

    // Block detail modal data
    const blockDisplayInfo = selectedBlockRows
        ? (() => {
            const bgtRows = selectedBlockRows.filter(r => r.work_bgt > 0);
            const actRows = selectedBlockRows.filter(r => r.work_act > 0);
            const totalBgt = bgtRows.reduce((s, r) => s + r.work_bgt, 0);
            const totalAct = actRows.reduce((s, r) => s + r.work_act, 0);
            const achievement = totalBgt > 0 ? (totalAct / totalBgt) * 100 : 0;
            return { totalBgt, totalAct, achievement, uom: selectedBlockRows[0]?.uom ?? '' };
        })()
        : null;

    return (
        <Box h="100dvh" display="flex" flexDirection="column">
            <Navbar />

            <Box flex="1" position="relative" overflow="hidden">
                {/* Sidebar */}
                <FilterUpkeepMap
                    filters={filters}
                    onApply={handleApply}
                    onClear={handleClear}
                    loading={loading}
                    isOpen={sidebarOpen}
                />

                {/* Sidebar toggle button */}
                <IconButton
                    aria-label="Toggle sidebar"
                    icon={sidebarOpen ? <MdOutlineKeyboardArrowLeft size={20} /> : <MdOutlineKeyboardArrowRight size={20} />}
                    position="fixed"
                    left={{
                        base: sidebarOpen ? 'calc(100% - 120px)' : '10px',
                        md: sidebarOpen ? '360px' : '10px',
                    }}
                    top={{ base: '68px', md: '48px' }}
                    zIndex={20}
                    w="40px"
                    h="40px"
                    minW="40px"
                    borderRadius="md"
                    boxShadow="md"
                    bg="customGreen.500"
                    color="white"
                    _hover={{ bg: 'customGreen.600' }}
                    _active={{ bg: 'customGreen.600' }}
                    transition="left 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                    onClick={() => setSidebarOpen(prev => !prev)}
                />

                {/* Map container */}
                <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    ml={{ base: 0, md: sidebarOpen ? '350px' : 0 }}
                    transition="margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                >
                    <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

                    {/* Loading overlay */}
                    {loading && (
                        <Center
                            position="absolute"
                            top={0} left={0} right={0} bottom={0}
                            bg="blackAlpha.300"
                            zIndex={10}
                        >
                            <Spinner size="xl" color="white" thickness="4px" />
                        </Center>
                    )}

                    {/* Map controls */}
                    <Flex
                        position="absolute"
                        bottom={{ base: 4, sm: 6, md: 8 }}
                        right={{ base: 4, sm: 3, md: 8 }}
                        direction="column"
                        align="center"
                        gap={2}
                        zIndex={10}
                    >
                        <Tooltip label={isSatellite ? 'Peta' : 'Satelit'} placement="left">
                            <IconButton
                                aria-label="Toggle satellite"
                                icon={<Text fontSize="x-small" fontWeight="bold">{isSatellite ? 'Peta' : 'Satelit'}</Text>}
                                colorScheme="blue"
                                variant="solid"
                                w="40px"
                                h="40px"
                                minW="40px"
                                borderRadius="full"
                                boxShadow="lg"
                                onClick={toggleSatellite}
                            />
                        </Tooltip>

                        <Tooltip label="Reset posisi" placement="left">
                            <IconButton
                                aria-label="Reset map"
                                icon={<MdMyLocation size={20} />}
                                colorScheme="blue"
                                variant="solid"
                                w="40px"
                                h="40px"
                                minW="40px"
                                borderRadius="full"
                                boxShadow="lg"
                                onClick={() => map?.flyTo({ center: MAP_CENTER, zoom: MAP_ZOOM })}
                            />
                        </Tooltip>

                        <ButtonGroup
                            isAttached
                            variant="solid"
                            colorScheme="blue"
                            orientation="vertical"
                            boxShadow="lg"
                            borderRadius="full"
                        >
                            <Tooltip label="Zoom in" placement="left">
                                <IconButton
                                    aria-label="Zoom in"
                                    icon={<FiPlus size={20} />}
                                    w="40px"
                                    h="40px"
                                    minW="40px"
                                    borderBottomRadius={0}
                                    onClick={() => map?.zoomIn()}
                                />
                            </Tooltip>
                            <Tooltip label="Zoom out" placement="left">
                                <IconButton
                                    aria-label="Zoom out"
                                    icon={<FiMinus size={20} />}
                                    w="40px"
                                    h="40px"
                                    minW="40px"
                                    borderTopRadius={0}
                                    onClick={() => map?.zoomOut()}
                                />
                            </Tooltip>
                        </ButtonGroup>
                    </Flex>

                    <Box position="absolute" bottom={4} left={4} zIndex={5}>
                        <UpkeepMapLegend />
                    </Box>

                    {!loading && data.length === 0 && (
                        <Box
                            position="absolute"
                            top={4}
                            left="50%"
                            transform="translateX(-50%)"
                            bg="white"
                            px={4}
                            py={2}
                            borderRadius="lg"
                            boxShadow="md"
                            zIndex={5}
                        >
                            <Text fontSize="sm" color="gray.500">
                                Pilih Activity dan klik Terapkan Filter untuk melihat data.
                            </Text>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Block Detail Modal */}
            <Modal
                isOpen={!!selectedBlockCode}
                onClose={() => { setSelectedBlockCode(null); setSelectedBlockRows(null); }}
                size={{ base: 'lg', md: 'xl' }}
                isCentered
                scrollBehavior="inside"
            >
                <ModalOverlay bg="blackAlpha.300" />
                <ModalContent borderRadius="lg" overflow="hidden">
                    <ModalHeader bg="customGold.500" borderBottomWidth="1px" borderColor="gray.100" py={3}>
                        <Flex direction="column" gap={1}>
                            <Text color="white" fontSize="lg" fontWeight="bold">
                                Blok {selectedBlockCode}
                            </Text>
                            {selectedBlockRows && selectedBlockRows[0] && (
                                <>
                                    <Text color="whiteAlpha.900" fontSize="xs">
                                        {selectedBlockRows[0].coa_group}
                                    </Text>
                                    <Text color="whiteAlpha.800" fontSize="xs">
                                        {selectedBlockRows[0].coa} — {selectedBlockRows[0].activity}
                                    </Text>
                                </>
                            )}
                        </Flex>
                    </ModalHeader>
                    <ModalCloseButton color="white" />

                    <ModalBody py={4}>
                        {selectedBlockRows && blockDisplayInfo && (
                            <Flex direction="column" gap={4}>
                                <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                                    <GridItem>
                                        <Text fontSize="xs" color="gray.500">Luas Tanam</Text>
                                        <Text fontSize="sm" fontWeight="medium">
                                            {selectedBlockRows[0].planted_area.toFixed(2)} Ha
                                        </Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text fontSize="xs" color="gray.500">Pencapaian</Text>
                                        <Text fontSize="sm" fontWeight="medium">
                                            {blockDisplayInfo.achievement.toFixed(1)}%
                                        </Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text fontSize="xs" color="gray.500">Total Budget</Text>
                                        <Text fontSize="sm" fontWeight="medium">
                                            {blockDisplayInfo.totalBgt.toFixed(2)} {blockDisplayInfo.uom}
                                        </Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text fontSize="xs" color="gray.500">Total Realisasi</Text>
                                        <Text fontSize="sm" fontWeight="medium">
                                            {blockDisplayInfo.totalAct.toFixed(2)} {blockDisplayInfo.uom}
                                        </Text>
                                    </GridItem>
                                </Grid>

                                <Box>
                                    <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={2} textTransform="uppercase">
                                        Detail Per Periode
                                    </Text>
                                    <Flex direction="column" gap={2}>
                                        {selectedBlockRows
                                            .slice()
                                            .sort((a, b) => a.periode.localeCompare(b.periode))
                                            .map((row, idx) => (
                                                <Box
                                                    key={idx}
                                                    p={3}
                                                    borderRadius="md"
                                                    border="1px solid"
                                                    borderColor="gray.100"
                                                    bg="gray.50"
                                                >
                                                    <Flex justify="space-between" align="center" mb={2}>
                                                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                                                            {formatPeriode(row.periode)}
                                                        </Text>
                                                        <Badge
                                                            colorScheme={
                                                                row.work_bgt > 0 && Math.round(row.work_act * 100) >= Math.round(row.work_bgt * 100)
                                                                    ? 'green'
                                                                    : row.work_act > 0
                                                                    ? 'orange'
                                                                    : 'gray'
                                                            }
                                                            fontSize="xs"
                                                        >
                                                            {row.work_bgt > 0 && Math.round(row.work_act * 100) >= Math.round(row.work_bgt * 100)
                                                                ? 'Tercapai'
                                                                : row.work_act > 0
                                                                ? 'Sebagian'
                                                                : 'Belum'}
                                                        </Badge>
                                                    </Flex>
                                                    <Grid templateColumns="repeat(2, 1fr)" gap={1}>
                                                        <GridItem>
                                                            <Text fontSize="xs" color="gray.500">Budget Kerja</Text>
                                                            <Text fontSize="xs" fontWeight="medium">
                                                                {row.work_bgt.toFixed(2)} {row.uom}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem>
                                                            <Text fontSize="xs" color="gray.500">Realisasi Kerja</Text>
                                                            <Text fontSize="xs" fontWeight="medium">
                                                                {row.work_act.toFixed(2)} {row.uom}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem>
                                                            <Text fontSize="xs" color="gray.500">HK Budget</Text>
                                                            <Text fontSize="xs" fontWeight="medium">
                                                                {row.hk_bgt.toFixed(2)}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem>
                                                            <Text fontSize="xs" color="gray.500">HK Realisasi</Text>
                                                            <Text fontSize="xs" fontWeight="medium">
                                                                {row.hk_act.toFixed(2)}
                                                            </Text>
                                                        </GridItem>
                                                        {row.work_act2 > 0 && (
                                                            <GridItem colSpan={2}>
                                                                <Text fontSize="xs" color="gray.500">Realisasi Alt ({row.uom2})</Text>
                                                                <Text fontSize="xs" fontWeight="medium">
                                                                    {row.work_act2.toFixed(2)}
                                                                </Text>
                                                            </GridItem>
                                                        )}
                                                    </Grid>
                                                </Box>
                                            ))}
                                    </Flex>
                                </Box>
                            </Flex>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default UpkeepMapPage;
